import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  IconButton,
  PaletteMode,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  Drawer,
} from "@mui/material";
import { Link } from 'react-router-dom';
import logoDark from "../../assets/logoDark.png";
import logoLight from "../../assets/logoLight.png";
import ListIcon from "@mui/icons-material/List";
import MapIcon from "@mui/icons-material/Map";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Tune } from "@mui/icons-material";
import {
  FilterButton,
  HeaderContainer,
  HeaderOptions,
  Logo,
  MobileMenuButton,
  MobileDrawerContent,
} from "./Header.styles";
import ApplyButton from "components/ApplyButton";
import ThemeSelector from "components/ThemeSelector";

/**
 * Header component with logo, theme selector, and navigation controls
 * Responsive with mobile hamburger menu
 */
const Header = ({
  setMode,
  view,
  handleViewChange,
  setIsSidebarOpen,
  isSidebarOpen,
  alignWithContainer,
  filtersActive,
  onBookVisit,
}: {
  setMode?: (mode: PaletteMode) => void;
  view?: "map" | "list";
  handleViewChange?: (
    event: React.MouseEvent<HTMLElement>,
    newView: "list" | "map"
  ) => void;
  setIsSidebarOpen?: (open: boolean) => void;
  isSidebarOpen?: boolean;
  alignWithContainer?: boolean;
  filtersActive?: boolean;
  onBookVisit?: () => void;
}) => {
  const theme = useTheme();
  const logo = theme.palette.mode === "dark" ? logoDark : logoLight;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // If pathname is /listings/:id or /listings/:id/... extract id
  const match = location.pathname.match(/^\/listings\/(?:([^\/]+))(?:\/|$)/);
  const listingId = match ? match[1] : null;
  const isBookingPage = location.pathname.endsWith("/book");

  return (
    <HeaderContainer alignWithContainer={alignWithContainer}>
      <Link to="/listings" style={{ display: 'inline-flex', alignItems: 'center' }} onClick={(e) => { handleViewChange?.(e as any, 'list'); }}>
        <Logo src={logo} />
      </Link>
      
      {/* Desktop Options */}
      <HeaderOptions>
        {!isBookingPage && (
          <Button
            variant="outlined"
            size="large"
            color="inherit"
            onClick={() => {
              if (listingId) navigate(`/listings/${listingId}/book`);
              else if (onBookVisit) onBookVisit();
            }}
            sx={{ minHeight: 48, padding: "10px 24px", fontSize: "1rem", textTransform: 'uppercase' }}
          >
            BOOK A VISIT
          </Button>
        )}

        <ApplyButton />

        {setMode && <ThemeSelector setMode={setMode} />}

        {setIsSidebarOpen && (
          <FilterButton
            active={filtersActive}
            color="primary"
            size="large"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            <Tune fontSize="large" />
          </FilterButton>
        )}

        {handleViewChange && (
          <ToggleButtonGroup
            size="large"
            value={view}
            exclusive
            onChange={handleViewChange}
          >
            <ToggleButton value="list">
              <ListIcon fontSize="large" />
            </ToggleButton>
            <ToggleButton value="map">
              <MapIcon fontSize="large" />
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </HeaderOptions>

      {/* Mobile Hamburger Menu */}
      <MobileMenuButton
        color="inherit"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
      </MobileMenuButton>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <MobileDrawerContent>
          <IconButton
            onClick={() => setMobileMenuOpen(false)}
            sx={{ alignSelf: 'flex-end', marginBottom: 1 }}
          >
            <CloseIcon />
          </IconButton>

          {!isBookingPage && (
            <Button
              variant="outlined"
              size="large"
              color="inherit"
              onClick={() => {
                setMobileMenuOpen(false);
                if (listingId) navigate(`/listings/${listingId}/book`);
                else if (onBookVisit) onBookVisit();
              }}
              sx={{ minHeight: 48, padding: "10px 24px", fontSize: "1rem", marginBottom: 1, textTransform: 'uppercase' }}
            >
              BOOK A VISIT
            </Button>
          )}

          <ApplyButton />

          {setMode && <ThemeSelector setMode={setMode} />}

          {setIsSidebarOpen && (
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => {
                setIsSidebarOpen(!isSidebarOpen);
                setMobileMenuOpen(false);
              }}
              startIcon={<Tune />}
              sx={filtersActive ? { border: `3px solid ${theme.palette.warning.main}` } : undefined}
            >
              Filters
            </Button>
          )}

          {handleViewChange && (
            <ToggleButtonGroup
              size="large"
              value={view}
              exclusive
              onChange={(e, v) => {
                handleViewChange?.(e, v);
                setMobileMenuOpen(false);
              }}
              fullWidth
            >
              <ToggleButton value="list">
                <ListIcon /> List
              </ToggleButton>
              <ToggleButton value="map">
                <MapIcon /> Map
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </MobileDrawerContent>
      </Drawer>
    </HeaderContainer>
  );
};

export default Header;
